import { api, store } from '../api/client';
import { problemOf } from './answer';
import { decidePrint, isHeld, type DecideOutcome, type Decision, type PrintJob } from './printing';

/** A kick the store holds for a decision (#515), with when it was made, by the store's clock. */
export interface HeldKick {
	kind: 'uncertain';
	createdAt: string;
}

export type DrawerResult = 'opened' | 'failed' | 'stopped' | HeldKick;

/**
 * Whether the store holds this kick for a decision. The drawer paths answer
 * the server's job body, which has no delivery, so the print queue's own list
 * says it. No answer reads as not held: Done then offers Retry, as before.
 */
async function heldKick(jobId: string): Promise<PrintJob | null> {
	try {
		const { data, error } = await store.GET('/store/print-jobs');
		if (error !== undefined || !data) return null;
		return (
			data.jobs.find((job) => job.id === jobId && job.document === 'drawer' && isHeld(job)) ?? null
		);
	} catch {
		return null;
	}
}

/**
 * Watch the store's kick for at most 35 seconds; closing Done cancels it. A
 * kick that failed is looked up in the print queue's list once, so one held
 * because its delivery is uncertain ends as its own result.
 */
export async function watchDrawer(jobId: string, signal: AbortSignal): Promise<DrawerResult> {
	if (signal.aborted) return 'stopped';
	let stopped = false;
	let pollTimer: ReturnType<typeof setTimeout> | undefined;
	let wake: (() => void) | undefined;
	let finish!: (result: DrawerResult) => void;
	const ended = new Promise<DrawerResult>((resolve) => (finish = resolve));
	const abort = () => finish('stopped');
	signal.addEventListener('abort', abort, { once: true });
	const deadline = setTimeout(() => finish('failed'), 35_000);
	async function poll(): Promise<DrawerResult> {
		while (!stopped) {
			try {
				const { data, error } = await api.GET('/api/till/hardware/jobs/{jobId}', {
					params: { path: { jobId } },
					signal,
				});
				if (stopped) return 'stopped';
				if (error === undefined && data) {
					if (data.state === 'done') return 'opened';
					if (data.state === 'failed' || data.state === 'expired') {
						const held = await heldKick(jobId);
						if (stopped) return 'stopped';
						return held ? { kind: 'uncertain', createdAt: held.createdAt } : 'failed';
					}
				}
			} catch {
				// A lost read says nothing about the kick: keep watching.
			}
			if (stopped) return 'stopped';
			await new Promise<void>((resolve) => {
				wake = resolve;
				pollTimer = setTimeout(resolve, 1000);
			});
		}
		return 'stopped';
	}
	try {
		return await Promise.race([poll(), ended]);
	} finally {
		stopped = true;
		clearTimeout(deadline);
		clearTimeout(pollTimer);
		wake?.();
		signal.removeEventListener('abort', abort);
	}
}

export async function retryDrawer(jobId: string): Promise<string | null> {
	try {
		const { data, error } = await api.POST('/api/till/drawer/opens', { body: { retryOf: jobId } });
		return error === undefined && data ? data.id : null;
	} catch {
		return null;
	}
}

/**
 * Open drawer now, for a held kick within its 2 minutes (#515): the decision
 * makes one new kick, a retry of the held one, whose id it returns for Done
 * to watch as Retry watches its retry. `decide` is the call that sends the
 * decision, so the till can count it as on its way.
 */
export async function openDrawerNow(
	jobId: string,
	decide: (jobId: string, decision: Decision) => Promise<DecideOutcome | null> = decidePrint,
): Promise<
	{ kind: 'opened'; jobId: string } | { kind: 'refused'; code: string } | { kind: 'unreachable' }
> {
	const outcome = await decide(jobId, 'open_drawer_now');
	if (outcome === null) return { kind: 'unreachable' };
	if (outcome.kind === 'refused') return outcome;
	const kick = outcome.job.decision?.replacementJobId;
	return kick ? { kind: 'opened', jobId: kick } : { kind: 'refused', code: '' };
}

export async function openDrawerNoSale(): Promise<
	{ kind: 'opened'; jobId: string } | { kind: 'refused'; code: string } | { kind: 'unreachable' }
> {
	try {
		const { data, error, response } = await api.POST('/api/till/drawer/opens', { body: {} });
		if (error === undefined && data) return { kind: 'opened', jobId: data.id };
		return { kind: 'refused', code: problemOf(error)?.code ?? String(response.status) };
	} catch {
		return { kind: 'unreachable' };
	}
}
